import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Star as StarIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Edit as EditIcon,
  LocationOn as LocationOnIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import UserRestrictionsDialog from '../../components/UserRestrictionsDialog';

interface UserProfileData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  role: string;
  businessId: string;
  businessName?: string;
  locationId?: string;
  locationName?: string;
  isActive: boolean;
  isPlatformUser: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  profileImage?: string;
  permissions: Record<string, boolean>;
  platformPermissions?: Record<string, boolean>;
  jobTags: Array<{
    id: string;
    name: string;
    description: string;
  }>;
  achievements: Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    points: number;
    earnedAt: string;
  }>;
  recentChecks: Array<{
    id: string;
    name: string;
    status: string;
    dueDate: string;
    completedAt?: string;
    location: string;
  }>;
}

/** Decode a field from a JWT without verifying signature (client-side display only). */
function decodeTokenField(token: string | null, field: string): string {
  try {
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.[field] || '';
  } catch { return ''; }
}

const UserProfile: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Restrictions state
  const [restrictions, setRestrictions] = useState<Record<string, boolean>>({});
  const [scope, setScope] = useState<{ allowedLocations: any[]; allowedAssetTypes: any[]; allowedAssets: any[] }>({
    allowedLocations: [], allowedAssetTypes: [], allowedAssets: [],
  });
  const [restrictionsDialogOpen, setRestrictionsDialogOpen] = useState(false);

  const token = localStorage.getItem('token');
  const viewerRole = decodeTokenField(token, 'role');
  const isAdminOrAbove = ['Admin', 'Super Admin', 'Owner', 'Multi Business Manager'].includes(viewerRole);
  const canEditLogin = viewerRole === 'Super Admin' || viewerRole === 'Owner';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const authToken = localStorage.getItem('token');
        // Decode userId from the token so we can call the parameterised endpoints
        const selfId = decodeTokenField(authToken, 'id');

        const [profileRes, restrictRes, scopeRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          selfId
            ? axios.get(`${API_BASE_URL}/users/${selfId}/restrictions`, {
                headers: { Authorization: `Bearer ${authToken}` },
              })
            : Promise.resolve({ data: { userModuleRestrictions: {} } }),
          selfId
            ? axios.get(`${API_BASE_URL}/users/${selfId}/scope`, {
                headers: { Authorization: `Bearer ${authToken}` },
              })
            : Promise.resolve({ data: {} }),
        ]);

        if (profileRes.status === 'fulfilled') {
          setProfile(profileRes.value.data);
          setFormData({
            firstName: profileRes.value.data.firstName || '',
            lastName: profileRes.value.data.lastName || '',
            email: profileRes.value.data.email || '',
            username: profileRes.value.data.username || '',
          });
        } else {
          const err = profileRes.reason;
          if (err?.response?.status === 401) {
            setError('Your session has expired. Please log in again.');
            setTimeout(() => { localStorage.removeItem('token'); window.location.href = '/'; }, 2000);
          } else {
            setError(err?.response?.data?.message || 'Failed to fetch profile data');
          }
        }

        if (restrictRes.status === 'fulfilled' && restrictRes.value) {
          setRestrictions(restrictRes.value.data?.userModuleRestrictions ?? {});
        }
        if (scopeRes.status === 'fulfilled' && scopeRes.value) {
          const sd = scopeRes.value.data ?? {};
          setScope({
            allowedLocations: sd.allowedLocations ?? [],
            allowedAssetTypes: sd.allowedAssetTypes ?? [],
            allowedAssets: sd.allowedAssets ?? [],
          });
        }
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to fetch profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaveError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      await axios.put(
        `${API_BASE_URL}/users/profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSaveSuccess(true);
      setIsEditing(false);
      // Refresh profile data
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setProfile(response.data);
    } catch (err) {
      setSaveError('Failed to update profile');
      console.error('Error updating profile:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!profile) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        No profile data available
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Profile
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Grid container spacing={3}>
          {/* Profile Header */}
          <Grid item xs={12} display="flex" justifyContent="center">
            <Avatar
              src={profile.profileImage}
              sx={{
                width: 100,
                height: 100,
                bgcolor: 'primary.main',
                fontSize: '2.5rem',
              }}
            >
              {profile.firstName?.[0]?.toUpperCase()}
            </Avatar>
          </Grid>

          {/* Tags Card */}
          {profile.jobTags && profile.jobTags.length > 0 && (
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader
                  title="Tags"
                  avatar={<StarIcon color="primary" />}
                />
                <CardContent>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {profile.jobTags.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Achievements Card */}
          {profile.achievements && profile.achievements.length > 0 && (
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader
                  title="Achievements"
                  avatar={<StarIcon color="primary" />}
                />
                <CardContent>
                  <List>
                    {profile.achievements.map((achievement) => (
                      <ListItem key={achievement.id}>
                        <ListItemText
                          primary={achievement.name}
                          secondary={
                            <>
                              {achievement.description}
                              <br />
                              <Typography variant="caption" color="text.secondary">
                                Earned: {new Date(achievement.earnedAt).toLocaleDateString()}
                              </Typography>
                            </>
                          }
                        />
                        <Chip
                          label={`${achievement.points} pts`}
                          color="secondary"
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Recent Checks Table */}
          {profile.recentChecks && profile.recentChecks.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Recent Safety Checks"
                  avatar={<StarIcon color="primary" />}
                />
                <CardContent>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Check Name</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Completed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {profile.recentChecks.map((check) => (
                          <TableRow key={check.id}>
                            <TableCell>{check.name}</TableCell>
                            <TableCell>{check.location}</TableCell>
                            <TableCell>
                              <Chip
                                label={check.status}
                                color={
                                  check.status === 'completed'
                                    ? 'success'
                                    : check.status === 'overdue'
                                    ? 'error'
                                    : 'warning'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(check.dueDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {check.completedAt
                                ? new Date(check.completedAt).toLocaleDateString()
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Editable Fields */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Read-only Fields */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Role"
              value={profile.role || ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Business"
              value={profile.businessName || profile.businessId || ''}
              disabled
            />
          </Grid>
          {profile.locationName && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={profile.locationName}
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Status"
              value={profile.isActive ? 'Active' : 'Inactive'}
              disabled
            />
          </Grid>

          {/* Permissions Section */}
          {profile.permissions && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(profile.permissions).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value ? 'Yes' : 'No'}`}
                    color={value ? 'success' : 'default'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Platform Permissions Section */}
          {profile.platformPermissions && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Platform Permissions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(profile.platformPermissions).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value ? 'Yes' : 'No'}`}
                    color={value ? 'success' : 'default'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Access & Restrictions Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="h6">
                Access &amp; Restrictions
              </Typography>
              {isAdminOrAbove && profile && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setRestrictionsDialogOpen(true)}
                >
                  Edit Restrictions
                </Button>
              )}
            </Box>

            {/* Login access */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Login Access
            </Typography>
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <Tooltip title={restrictions.web_login ? 'Web portal login is blocked' : 'Can log in to web portal'}>
                <Chip
                  icon={restrictions.web_login ? <LockIcon /> : <LockOpenIcon />}
                  label={restrictions.web_login ? 'Web: Blocked' : 'Web: Allowed'}
                  color={restrictions.web_login ? 'error' : 'success'}
                  variant={restrictions.web_login ? 'filled' : 'outlined'}
                  size="small"
                />
              </Tooltip>
              <Tooltip title={restrictions.app_login ? 'Mobile app login is blocked' : 'Can log in to mobile app'}>
                <Chip
                  icon={restrictions.app_login ? <LockIcon /> : <LockOpenIcon />}
                  label={restrictions.app_login ? 'App: Blocked' : 'App: Allowed'}
                  color={restrictions.app_login ? 'error' : 'success'}
                  variant={restrictions.app_login ? 'filled' : 'outlined'}
                  size="small"
                />
              </Tooltip>
            </Box>

            {/* Active module/action restrictions */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Active Restrictions
            </Typography>
            {(() => {
              const activeKeys = Object.entries(restrictions)
                .filter(([k, v]) => v === true && k !== 'web_login' && k !== 'app_login')
                .map(([k]) => k);
              if (activeKeys.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    None — full access to all enabled modules.
                  </Typography>
                );
              }
              return (
                <Box display="flex" flexWrap="wrap" gap={0.75} mb={2}>
                  {activeKeys.map((k) => (
                    <Chip
                      key={k}
                      icon={<LockIcon />}
                      label={k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      color="warning"
                      size="small"
                    />
                  ))}
                </Box>
              );
            })()}

            {/* Scope restrictions */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <LocationOnIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" color="text.secondary">Location Scope</Typography>
                </Box>
                {scope.allowedLocations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">All locations</Typography>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {scope.allowedLocations.slice(0, 5).map((l: any) => (
                      <Chip key={l.location_id ?? l.id} label={l.location_name ?? l.label} size="small" variant="outlined" />
                    ))}
                    {scope.allowedLocations.length > 5 && (
                      <Chip label={`+${scope.allowedLocations.length - 5} more`} size="small" />
                    )}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <InventoryIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" color="text.secondary">Asset Type Scope</Typography>
                </Box>
                {scope.allowedAssetTypes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">All asset types</Typography>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {scope.allowedAssetTypes.map((t: any) => (
                      <Chip key={t.asset_type_id ?? t.id} label={t.type_name ?? t.label} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <InventoryIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" color="text.secondary">Specific Assets</Typography>
                </Box>
                {scope.allowedAssets.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">All assets</Typography>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {scope.allowedAssets.slice(0, 5).map((a: any) => (
                      <Chip key={a.asset_id ?? a.id} label={a.asset_name ?? a.label} size="small" variant="outlined" />
                    ))}
                    {scope.allowedAssets.length > 5 && (
                      <Chip label={`+${scope.allowedAssets.length - 5} more`} size="small" />
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* System Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              System Information
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Last Login"
                  secondary={profile.lastLoginAt ? new Date(profile.lastLoginAt).toLocaleString() : 'Never'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Account Created"
                  secondary={new Date(profile.createdAt).toLocaleString()}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Last Updated"
                  secondary={new Date(profile.updatedAt).toLocaleString()}
                />
              </ListItem>
            </List>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              {isEditing ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        firstName: profile.firstName || '',
                        lastName: profile.lastName || '',
                        email: profile.email || '',
                        username: profile.username || '',
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleSave}>
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button variant="contained" onClick={() => setIsEditing(true)}>
                  Edit Profile
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
        {saveSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Profile updated successfully
          </Alert>
        )}
      </Paper>

      {/* Restrictions Dialog — opened from "Edit Restrictions" button */}
      {profile && restrictionsDialogOpen && (
        <UserRestrictionsDialog
          open={restrictionsDialogOpen}
          onClose={() => setRestrictionsDialogOpen(false)}
          userId={profile.id}
          userName={`${profile.firstName} ${profile.lastName}`}
          canEdit={isAdminOrAbove}
          canEditLogin={canEditLogin}
        />
      )}
    </Box>
  );
};

export default UserProfile; 